import { ApolloProvider } from '@apollo/client';
import {
  ModalProvider,
  ToastContextProvider,
} from '@leland-dev/leland-ui-library';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { type NextWebVitalsMetric } from 'next/app';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import AuthContextProvider from '../context/AuthContext';
import LexContextProvider from '../context/LexContext';
import { useApollo } from '../lib/apolloClient';
import { useAttribution } from '../lib/attribution';
import { useGtm } from '../lib/gtm';
import { init as initNProgress } from '../lib/nprogress';
import { appInsights, reactPlugin } from '../utils/analytics/app-insights';
import phog from '../utils/analytics/posthog/posthog';
import { usePageView } from '../utils/analytics/usePageView';
import { supabase } from '../utils/supabase/component';

/* eslint-disable import/order */
import 'nprogress/nprogress.css'; //styles of nprogress
import '../styles/globals.css';
/* eslint-enable import/order */

export function reportWebVitals(metric: NextWebVitalsMetric) {
  appInsights.trackMetric(
    { name: metric.name, average: metric.value },
    {
      url: window.location.href,
    },
  );
}

export default function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  /* eslint-disable */
  const deleteCookies = async () => {
    try {
      // @ts-ignore
      const cookies = await cookieStore.getAll();
      // @ts-ignore
      const cookiesToRemove = cookies.filter((cook) =>
        cook.name?.includes('sb-'),
      );

      for (const cook of cookiesToRemove) {
        // @ts-ignore
        await cookieStore.delete(cook);
      }
    } catch (e) {
      console.error('Error deleting cookies:', e);
    }
  };
  /* eslint-enable */

  useEffect(() => {
    // @ts-expect-error `lelandVersion` does not exist on `window`
    window.lelandVersion = process.env.NEXT_PUBLIC_LELAND_VERSION;
  }, []);

  const apolloClient = useApollo(pageProps);

  useGtm();

  useAttribution();

  usePageView();

  useEffect(() => {
    initNProgress();
    void phog.init();
  }, []);

  // this is a one time client fix for duplicate cookies. We should remove this ASAP after August 2nd, 2025
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        console.log('checking session', data, error);
        if (error?.message.includes('rate limit')) {
          console.log('kill the rate limit cookies');
          await deleteCookies();
          console.log('cookies killed');
          return;
        }
      } catch (e) {
        console.error('Error refreshing session', e);
      }
    };
    void setTimeout(() => {
      void checkSession();
    }, 5000);
  }, []);

  const router = useRouter();

  // Use layout defined at page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <ApolloProvider client={apolloClient}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <AuthContextProvider>
          <LexContextProvider>
            <ToastContextProvider>
              <ModalProvider pathname={router.asPath}>
                {getLayout(<Component {...pageProps} />, pageProps)}
              </ModalProvider>
            </ToastContextProvider>
          </LexContextProvider>
        </AuthContextProvider>
      </AppInsightsContext.Provider>
    </ApolloProvider>
  );
}
